<template>
  <div class="trend-chart">
    <div v-if="!data || data.length === 0" class="text-sm text-gray-400 text-center py-6">
      No trend data available
    </div>
    <div v-else>
      <!-- Chart area -->
      <svg :viewBox="`0 0 ${width} ${height}`" class="w-full" preserveAspectRatio="xMidYMid meet">
        <!-- Y-axis grid lines -->
        <line
          v-for="tick in yTicks"
          :key="'grid-' + tick"
          :x1="padding.left"
          :y1="yScale(tick)"
          :x2="width - padding.right"
          :y2="yScale(tick)"
          stroke="#e5e7eb"
          stroke-dasharray="3,3"
        />
        <!-- Y-axis labels -->
        <text
          v-for="tick in yTicks"
          :key="'label-' + tick"
          :x="padding.left - 6"
          :y="yScale(tick) + 3"
          text-anchor="end"
          class="text-[9px] fill-gray-400"
        >
          {{ tick }}
        </text>

        <!-- Stacked bars -->
        <g v-for="(day, i) in data" :key="day.date">
          <!-- Bugs (bottom) -->
          <rect
            v-if="day.bugs > 0"
            :x="barX(i)"
            :y="yScale(day.bugs + day.features + day.other)"
            :width="barWidth"
            :height="yScale(0) - yScale(day.bugs)"
            fill="#ef4444"
            rx="2"
          />
          <!-- Features (middle) -->
          <rect
            v-if="day.features > 0"
            :x="barX(i)"
            :y="yScale(day.features + day.other)"
            :width="barWidth"
            :height="yScale(0) - yScale(day.features)"
            fill="#6366f1"
            rx="2"
          />
          <!-- Other (top) -->
          <rect
            v-if="day.other > 0"
            :x="barX(i)"
            :y="yScale(day.other)"
            :width="barWidth"
            :height="yScale(0) - yScale(day.other)"
            fill="#9ca3af"
            rx="2"
          />

          <!-- X-axis label -->
          <text
            :x="barX(i) + barWidth / 2"
            :y="height - 4"
            text-anchor="middle"
            class="text-[9px] fill-gray-400"
          >
            {{ day.date }}
          </text>

          <!-- Total count above bar -->
          <text
            v-if="day.total > 0"
            :x="barX(i) + barWidth / 2"
            :y="yScale(day.total) - 4"
            text-anchor="middle"
            class="text-[8px] fill-gray-500 font-medium"
          >
            {{ day.total }}
          </text>
        </g>
      </svg>

      <!-- Legend -->
      <div class="flex items-center gap-4 justify-center pt-2">
        <div class="flex items-center gap-1.5">
          <span class="w-2.5 h-2.5 rounded-sm bg-red-500"></span>
          <span class="text-xs text-gray-500">Bugs</span>
        </div>
        <div class="flex items-center gap-1.5">
          <span class="w-2.5 h-2.5 rounded-sm bg-indigo-500"></span>
          <span class="text-xs text-gray-500">Features</span>
        </div>
        <div class="flex items-center gap-1.5">
          <span class="w-2.5 h-2.5 rounded-sm bg-gray-400"></span>
          <span class="text-xs text-gray-500">Other</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface TrendBucket {
  date: string
  total: number
  bugs: number
  features: number
  other: number
}

const props = defineProps<{
  data: TrendBucket[]
}>()

const width = 400
const height = 160
const padding = { top: 16, right: 12, bottom: 20, left: 28 }

const maxValue = computed(() => {
  if (!props.data || props.data.length === 0) return 1
  return Math.max(...props.data.map(d => d.total), 1)
})

const yTicks = computed(() => {
  const max = maxValue.value
  if (max <= 5) return Array.from({ length: max + 1 }, (_, i) => i)
  const step = Math.ceil(max / 4)
  const ticks = []
  for (let i = 0; i <= max; i += step) ticks.push(i)
  return ticks
})

const chartWidth = computed(() => width - padding.left - padding.right)

const barWidth = computed(() => {
  if (!props.data || props.data.length === 0) return 0
  const gap = 6
  return Math.min(32, (chartWidth.value - gap * (props.data.length - 1)) / props.data.length)
})

function barX(index) {
  if (!props.data) return 0
  const totalBars = props.data.length
  const totalBarWidth = barWidth.value * totalBars
  const totalGap = chartWidth.value - totalBarWidth
  const gap = totalGap / (totalBars - 1 || 1)
  return padding.left + index * (barWidth.value + gap)
}

function yScale(value) {
  const chartHeight = height - padding.top - padding.bottom
  return height - padding.bottom - (value / maxValue.value) * chartHeight
}
</script>
